import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatDate as formatDateUtil, formatDateTime } from '../utils/dateFormat';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, DialogBody } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { 
  CheckSquare,
  Plus,
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar,
  User,
  Loader2,
  ChevronRight,
  Edit,
  Trash2,
  MessageSquare,
  Link as LinkIcon,
  Flag,
  Paperclip,
  Upload,
  Download,
  X,
  FileText,
  Repeat,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Roles that can assign tasks to others
const TASK_ASSIGN_ROLES = ['system_admin', 'executive', 'operations_manager'];
// Roles that can create recurring tasks
const RECURRING_TASK_ROLES = ['system_admin', 'executive', 'operations_manager'];

const TasksWidget = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [taskSummary, setTaskSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('open');
  
  // Task templates
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('none');
  
  // Create task dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    assigned_to: 'self',
    participant_id: 'none',
    // Multiple assignees
    assigned_to_list: [],
    use_multi_assignee: false,
    // Recurring task options
    is_recurring: false,
    recurrence_pattern: '',
    recurrence_end_date: ''
  });
  
  // Edit task dialog
  const [editingTask, setEditingTask] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingEditAttachments, setPendingEditAttachments] = useState([]);  // For edit task attachments
  
  // Reopen task comment dialog (for admins reopening completed tasks)
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [reopeningComment, setReopeningComment] = useState('');
  const [pendingReopenStatus, setPendingReopenStatus] = useState(null);
  const [originalTaskStatus, setOriginalTaskStatus] = useState(null);
  
  // Add note dialog
  const [addingNoteTask, setAddingNoteTask] = useState(null);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [noteContent, setNoteContent] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  // Attachment states
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [showAttachmentsDialog, setShowAttachmentsDialog] = useState(false);
  const [viewingTask, setViewingTask] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);  // For new task creation
  const [isDragging, setIsDragging] = useState(false);  // For drag-and-drop
  const [isDraggingEdit, setIsDraggingEdit] = useState(false);  // For edit dialog drag-and-drop
  const [uploadProgress, setUploadProgress] = useState({});  // Track upload progress per file
  
  const canAssignToOthers = TASK_ASSIGN_ROLES.includes(user?.role);
  
  // Dynamic dropdown values
  const [taskPriorities, setTaskPriorities] = useState([
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'high', label: 'High' },
    { value: 'urgent', label: 'Urgent' },
  ]);
  const [taskStatuses, setTaskStatuses] = useState([
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ]);

  useEffect(() => {
    fetchTasks();
    fetchTaskSummary();
    fetchTaskTemplates();
    fetchDropdowns();
    if (canAssignToOthers) {
      fetchStaffList();
    }
  }, [activeTab]);

  const fetchTasks = async () => {
    try {
      // Use my_tasks_only=true to only show tasks assigned TO the current user
      // (not tasks they created for others)
      let url = `${API}/tasks?limit=20&my_tasks_only=true`;
      if (activeTab === 'completed') {
        url += '&status=completed';
      } else if (activeTab === 'overdue') {
        url += '&status=overdue';
      } else {
        url += '&status=open';
      }
      const response = await axios.get(url);
      setTasks(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTaskSummary = async () => {
    try {
      const response = await axios.get(`${API}/tasks/summary`);
      setTaskSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch task summary:', error);
    }
  };
  
  const fetchTaskTemplates = async () => {
    try {
      const response = await axios.get(`${API}/task-templates`);
      setTaskTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch task templates:', error);
    }
  };
  
  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    if (templateId && templateId !== 'none') {
      const template = taskTemplates.find(t => t.id === templateId);
      if (template) {
        setTaskForm(prev => ({
          ...prev,
          title: template.name,  // Default title to template name
          description: template.description,
          priority: template.default_priority || prev.priority
        }));
      }
    }
  };

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/users/staff`);
      setStaffList(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch staff list:', error);
    }
  };

  const fetchDropdowns = async () => {
    try {
      const [prioritiesRes, statusesRes] = await Promise.all([
        axios.get(`${API}/dropdowns/task_priorities`),
        axios.get(`${API}/dropdowns/task_statuses`)
      ]);
      if (prioritiesRes.data.items?.length > 0) {
        setTaskPriorities(prioritiesRes.data.items);
      }
      if (statusesRes.data.items?.length > 0) {
        setTaskStatuses(statusesRes.data.items);
      }
    } catch (error) {
      console.error('Failed to fetch dropdowns:', error);
      // Keep default values if fetch fails
    }
  };

  const fetchParticipants = async (assignedToId = null) => {
    try {
      // If assigning to someone else, get their caseload participants
      // If assigning to self or no one selected, get current user's participants
      const staffId = assignedToId && assignedToId !== 'self' ? assignedToId : user?.id;
      const response = await axios.get(`${API}/participants?limit=50&assigned_staff_id=${staffId}`);
      setParticipants(response.data.items || []);
    } catch (error) {
      console.error('Failed to fetch participants:', error);
      setParticipants([]);
    }
  };

  // Fetch participants when assigned_to changes
  useEffect(() => {
    if (showCreateDialog) {
      fetchParticipants(taskForm.assigned_to);
      // Reset participant selection when assignee changes
      if (taskForm.participant_id !== 'none') {
        setTaskForm(prev => ({ ...prev, participant_id: 'none' }));
      }
    }
  }, [taskForm.assigned_to, showCreateDialog]);

  const handleCreateTask = async () => {
    if (!taskForm.title.trim()) {
      toast.error('Task title is required');
      return;
    }
    
    // Validate recurring task options
    if (taskForm.is_recurring && !taskForm.recurrence_pattern) {
      toast.error('Please select a recurrence pattern');
      return;
    }
    
    // Validate multi-assignee has at least one selection
    if (taskForm.use_multi_assignee && taskForm.assigned_to_list.length === 0) {
      toast.error('Please select at least one assignee');
      return;
    }
    
    setCreating(true);
    try {
      const payload = {
        title: taskForm.title,
        description: taskForm.description,
        priority: taskForm.priority,
        due_date: taskForm.due_date || undefined,
        participant_id: taskForm.participant_id === 'none' ? undefined : (taskForm.participant_id || undefined),
        // Handle assignees
        assigned_to: taskForm.use_multi_assignee 
          ? undefined 
          : (taskForm.assigned_to === 'self' ? undefined : (taskForm.assigned_to || undefined)),
        assigned_to_list: taskForm.use_multi_assignee && taskForm.assigned_to_list.length > 0 
          ? taskForm.assigned_to_list 
          : undefined,
        // Recurring task options
        is_recurring: taskForm.is_recurring || undefined,
        recurrence_pattern: taskForm.is_recurring ? taskForm.recurrence_pattern : undefined,
        recurrence_end_date: taskForm.is_recurring && taskForm.recurrence_end_date ? taskForm.recurrence_end_date : undefined
      };
      const response = await axios.post(`${API}/tasks`, payload);
      
      // Handle both single task and multi-task responses
      const newTaskId = response.data.id || response.data.tasks?.[0]?.id;
      const createdCount = response.data.total_created || 1;
      
      // Upload any pending attachments with progress tracking
      if (newTaskId && pendingAttachments.length > 0) {
        for (let i = 0; i < pendingAttachments.length; i++) {
          const file = pendingAttachments[i];
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axios.post(`${API}/tasks/${newTaskId}/attachments`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(prev => ({ ...prev, [i]: percentCompleted }));
              }
            });
          } catch (err) {
            console.error('Failed to upload attachment:', err);
            toast.error(`Failed to upload: ${file.name}`);
          }
        }
        // After all attachments uploaded, trigger notification
        try {
          await axios.post(`${API}/tasks/${newTaskId}/send-notification`);
        } catch (err) {
          console.error('Failed to send notification:', err);
        }
      }
      
      if (createdCount > 1) {
        toast.success(`Created ${createdCount} tasks for multiple assignees`);
      } else if (taskForm.is_recurring) {
        toast.success('Recurring task created successfully');
      } else {
        toast.success('Task created successfully');
      }
      setShowCreateDialog(false);
      setTaskForm({
        title: '',
        description: '',
        priority: 'medium',
        due_date: '',
        assigned_to: 'self',
        participant_id: 'none',
        assigned_to_list: [],
        use_multi_assignee: false,
        is_recurring: false,
        recurrence_pattern: '',
        recurrence_end_date: ''
      });
      setSelectedTemplate('none');
      setPendingAttachments([]);
      setUploadProgress({});
      fetchTasks();
      fetchTaskSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create task');
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateTask = async (reopenComment = null) => {
    if (!editingTask) return;
    
    // Check if this is a reopen scenario (completed -> open/in_progress by admin)
    const isReopening = originalTaskStatus === 'completed' && 
                       ['open', 'in_progress'].includes(editingTask.status) &&
                       canAssignToOthers;
    
    // If reopening and no comment provided, show the reopen dialog
    if (isReopening && !reopenComment && !showReopenDialog) {
      setPendingReopenStatus(editingTask.status);
      setShowReopenDialog(true);
      return;
    }
    
    setSaving(true);
    try {
      // Build update payload
      const updatePayload = { ...editingTask };
      if (isReopening && reopenComment) {
        updatePayload.reopening_comment = reopenComment;
      }
      
      // Update the task first
      await axios.put(`${API}/tasks/${editingTask.id}`, updatePayload);
      
      // Upload any pending attachments with progress tracking
      if (pendingEditAttachments.length > 0) {
        for (let i = 0; i < pendingEditAttachments.length; i++) {
          const file = pendingEditAttachments[i];
          const formData = new FormData();
          formData.append('file', file);
          try {
            await axios.post(`${API}/tasks/${editingTask.id}/attachments`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
              onUploadProgress: (progressEvent) => {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(prev => ({ ...prev, [`edit_${i}`]: percentCompleted }));
              }
            });
          } catch (err) {
            console.error('Failed to upload attachment:', err);
            toast.error(`Failed to upload: ${file.name}`);
          }
        }
      }
      
      toast.success(isReopening ? 'Task reopened successfully' : 'Task updated successfully');
      setShowEditDialog(false);
      setShowReopenDialog(false);
      setEditingTask(null);
      setOriginalTaskStatus(null);
      setPendingEditAttachments([]);
      setUploadProgress({});
      setReopeningComment('');
      setPendingReopenStatus(null);
      fetchTasks();
      fetchTaskSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    } finally {
      setSaving(false);
    }
  };
  
  // Handle the reopen comment submission
  const handleConfirmReopen = () => {
    if (!reopeningComment.trim()) {
      toast.error('Please enter a reason for reopening this task');
      return;
    }
    handleUpdateTask(reopeningComment);
  };
  
  // Cancel the reopen dialog and revert status
  const handleCancelReopen = () => {
    setShowReopenDialog(false);
    setReopeningComment('');
    setPendingReopenStatus(null);
    // Revert the status back to completed
    if (editingTask) {
      setEditingTask(prev => ({ ...prev, status: 'completed' }));
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus });
      toast.success(`Task ${newStatus === 'completed' ? 'completed' : 'updated'}`);
      fetchTasks();
      fetchTaskSummary();
    } catch (error) {
      toast.error('Failed to update task status');
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim() || !addingNoteTask) return;
    
    setAddingNote(true);
    try {
      await axios.post(`${API}/tasks/${addingNoteTask.id}/notes`, { content: noteContent });
      toast.success('Note added successfully');
      setShowNoteDialog(false);
      setNoteContent('');
      setAddingNoteTask(null);
      fetchTasks();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  const handleFileUpload = async (taskId, file) => {
    if (!file) return;
    
    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    
    setUploadingAttachment(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await axios.post(`${API}/tasks/${taskId}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('File uploaded successfully');
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async (taskId, attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskId}/attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      fetchTasks();
      // Update the viewing task if dialog is open
      if (viewingTask && viewingTask.id === taskId) {
        const updatedTask = await axios.get(`${API}/tasks/${taskId}`);
        setViewingTask(updatedTask.data);
      }
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const openAttachmentsDialog = (task) => {
    setViewingTask(task);
    setShowAttachmentsDialog(true);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Download attachment with authentication
  const handleDownloadAttachment = async (attachment) => {
    try {
      const url = attachment.file_url.startsWith('http') 
        ? attachment.file_url 
        : `${BACKEND_URL}${attachment.file_url}`;
      
      // Fetch with auth headers
      const response = await axios.get(url, { responseType: 'blob' });
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data]);
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = attachment.file_name || 'attachment';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download attachment:', error);
      toast.error('Failed to download attachment');
    }
  };

  // Handle multiple files for new task creation
  const handleAddPendingAttachments = (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    
    for (const file of fileArray) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
      } else {
        validFiles.push(file);
      }
    }
    
    if (validFiles.length > 0) {
      setPendingAttachments(prev => [...prev, ...validFiles]);
    }
  };

  const handleAddPendingAttachment = (file) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    setPendingAttachments(prev => [...prev, file]);
  };

  const removePendingAttachment = (index) => {
    setPendingAttachments(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[index];
      return newProgress;
    });
  };

  // Drag and drop handlers for new task
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleAddPendingAttachments(files);
    }
  };

  // Handle multiple files for edit task
  const handleAddEditAttachments = (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    
    for (const file of fileArray) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 10MB limit`);
      } else {
        validFiles.push(file);
      }
    }
    
    if (validFiles.length > 0) {
      setPendingEditAttachments(prev => [...prev, ...validFiles]);
    }
  };

  // Functions for edit task attachments
  const handleAddEditAttachment = (file) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size exceeds 10MB limit');
      return;
    }
    setPendingEditAttachments(prev => [...prev, file]);
  };

  const removeEditPendingAttachment = (index) => {
    setPendingEditAttachments(prev => prev.filter((_, i) => i !== index));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[`edit_${index}`];
      return newProgress;
    });
  };

  // Drag and drop handlers for edit task
  const handleDragOverEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingEdit(true);
  };

  const handleDragLeaveEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingEdit(false);
  };

  const handleDropEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingEdit(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleAddEditAttachments(files);
    }
  };

  const handleDeleteExistingAttachment = async (attachmentId) => {
    if (!editingTask || !window.confirm('Delete this attachment?')) return;
    
    try {
      await axios.delete(`${API}/tasks/${editingTask.id}/attachments/${attachmentId}`);
      // Update the editing task's attachments list
      setEditingTask(prev => ({
        ...prev,
        attachments: prev.attachments.filter(a => a.id !== attachmentId)
      }));
      toast.success('Attachment deleted');
    } catch (error) {
      toast.error('Failed to delete attachment');
    }
  };

  const openEditDialog = async (task) => {
    // Fetch fresh task data to get all details including attachments
    try {
      const response = await axios.get(`${API}/tasks/${task.id}`);
      setEditingTask(response.data);
      setOriginalTaskStatus(response.data.status);  // Track original status for reopen detection
    } catch (error) {
      // Fall back to the task data we have
      setEditingTask(task);
      setOriginalTaskStatus(task.status);  // Track original status for reopen detection
    }
    setPendingEditAttachments([]);
    setShowEditDialog(true);
  };

  const openViewDialog = async (task) => {
    // Fetch fresh task data to get all details
    try {
      const response = await axios.get(`${API}/tasks/${task.id}`);
      setViewingTask(response.data);
      setShowViewDialog(true);
    } catch (error) {
      // Fall back to the task data we have
      setViewingTask(task);
      setShowViewDialog(true);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success('Task deleted');
      fetchTasks();
      fetchTaskSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-500 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return formatDateUtil(dateStr) || '-';
  };

  const openNoteDialog = (task) => {
    setAddingNoteTask(task);
    setNoteContent('');
    setShowNoteDialog(true);
  };

  return (
    <Card className="card-shadow" data-testid="tasks-widget">
      <CardHeader className="flex flex-row items-center justify-between pb-4">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-primary" />
            My Tasks
          </CardTitle>
          <CardDescription>
            {taskSummary && (
              <span className="flex gap-3 mt-1">
                <span className="text-blue-600">{taskSummary.open} open</span>
                <span className="text-red-600">{taskSummary.overdue} overdue</span>
                <span className="text-green-600">{taskSummary.completed_recent} completed</span>
              </span>
            )}
          </CardDescription>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button size="sm" data-testid="create-task-btn">
              <Plus className="w-4 h-4 mr-1" />
              New Task
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create New Task</DialogTitle>
              <DialogDescription>
                Create a task for yourself{canAssignToOthers ? ' or assign it to a team member' : ''}
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-4 py-4">
              {/* Task Template Dropdown */}
              <div className="space-y-2">
                <Label>Use Template (Optional)</Label>
                <Select 
                  value={selectedTemplate} 
                  onValueChange={handleTemplateSelect}
                >
                  <SelectTrigger data-testid="task-template-select">
                    <SelectValue placeholder="Select a template..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No template - start fresh</SelectItem>
                    {taskTemplates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name} ({template.category})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Templates pre-fill the description and priority
                </p>
              </div>
              
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={taskForm.title}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter task title"
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Add details about this task..."
                  rows={3}
                  data-testid="task-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={taskForm.priority} 
                    onValueChange={(val) => setTaskForm(prev => ({ ...prev, priority: val }))}
                  >
                    <SelectTrigger data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {taskPriorities.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                    data-testid="task-due-date-input"
                  />
                </div>
              </div>
              {canAssignToOthers && (
                <div className="space-y-4">
                  {/* Multi-assignee toggle */}
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="use-multi-assignee"
                      checked={taskForm.use_multi_assignee}
                      onCheckedChange={(checked) => setTaskForm(prev => ({ 
                        ...prev, 
                        use_multi_assignee: checked,
                        assigned_to: checked ? 'self' : prev.assigned_to,
                        assigned_to_list: []
                      }))}
                      data-testid="multi-assignee-checkbox"
                    />
                    <Label htmlFor="use-multi-assignee" className="cursor-pointer flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Assign to multiple users
                    </Label>
                  </div>
                  
                  {!taskForm.use_multi_assignee ? (
                    <div className="space-y-2">
                      <Label>Assign To</Label>
                      <Select 
                        value={taskForm.assigned_to} 
                        onValueChange={(val) => setTaskForm(prev => ({ ...prev, assigned_to: val }))}
                      >
                        <SelectTrigger data-testid="task-assignee-select">
                          <SelectValue placeholder="Assign to myself" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="self">Myself</SelectItem>
                          {staffList.map(staff => (
                            <SelectItem key={staff.id} value={staff.id}>
                              {staff.name} ({staff.role?.replace(/_/g, ' ')})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label>Select Assignees</Label>
                      <div className="border rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                        {staffList.map(staff => (
                          <div key={staff.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`assignee-${staff.id}`}
                              checked={taskForm.assigned_to_list.includes(staff.id)}
                              onCheckedChange={(checked) => {
                                setTaskForm(prev => ({
                                  ...prev,
                                  assigned_to_list: checked 
                                    ? [...prev.assigned_to_list, staff.id]
                                    : prev.assigned_to_list.filter(id => id !== staff.id)
                                }));
                              }}
                              data-testid={`assignee-checkbox-${staff.id}`}
                            />
                            <Label htmlFor={`assignee-${staff.id}`} className="cursor-pointer text-sm">
                              {staff.name} <span className="text-muted-foreground">({staff.role?.replace(/_/g, ' ')})</span>
                            </Label>
                          </div>
                        ))}
                      </div>
                      {taskForm.assigned_to_list.length > 0 && (
                        <p className="text-xs text-muted-foreground">
                          {taskForm.assigned_to_list.length} user(s) selected - each will receive their own task instance
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {/* Recurring Task Options - Only for authorized roles */}
              {RECURRING_TASK_ROLES.includes(user?.role) && (
                <div className="space-y-4 border-t pt-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is-recurring"
                      checked={taskForm.is_recurring}
                      onCheckedChange={(checked) => setTaskForm(prev => ({ 
                        ...prev, 
                        is_recurring: checked,
                        recurrence_pattern: checked ? prev.recurrence_pattern : '',
                        recurrence_end_date: checked ? prev.recurrence_end_date : ''
                      }))}
                      data-testid="recurring-task-checkbox"
                    />
                    <Label htmlFor="is-recurring" className="cursor-pointer flex items-center gap-2">
                      <Repeat className="w-4 h-4" />
                      Make this a recurring task
                    </Label>
                  </div>
                  
                  {taskForm.is_recurring && (
                    <div className="ml-6 space-y-4">
                      <div className="space-y-2">
                        <Label>Recurrence Pattern</Label>
                        <Select 
                          value={taskForm.recurrence_pattern} 
                          onValueChange={(val) => setTaskForm(prev => ({ ...prev, recurrence_pattern: val }))}
                        >
                          <SelectTrigger data-testid="recurrence-pattern-select">
                            <SelectValue placeholder="Select pattern..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="daily">Daily</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>End Date (Optional)</Label>
                        <Input
                          type="date"
                          value={taskForm.recurrence_end_date}
                          onChange={(e) => setTaskForm(prev => ({ ...prev, recurrence_end_date: e.target.value }))}
                          min={taskForm.due_date || new Date().toISOString().split('T')[0]}
                          data-testid="recurrence-end-date-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Leave blank for no end date. New tasks will be created automatically.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <Label>
                  Link to Participant (Optional)
                  {taskForm.assigned_to && taskForm.assigned_to !== 'self' && (
                    <span className="text-xs text-muted-foreground ml-2">
                      - from assignee&apos;s caseload
                    </span>
                  )}
                </Label>
                <Select 
                  value={taskForm.participant_id} 
                  onValueChange={(val) => setTaskForm(prev => ({ ...prev, participant_id: val }))}
                >
                  <SelectTrigger data-testid="task-participant-select">
                    <SelectValue placeholder="Select participant..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No linked participant</SelectItem>
                    {participants.length === 0 ? (
                      <SelectItem value="none" disabled>
                        No participants in caseload
                      </SelectItem>
                    ) : (
                      participants.map(p => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.full_name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              {/* File Attachments Section */}
              <div className="space-y-2">
                <Label>Attachments (Optional)</Label>
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`relative border-2 border-dashed rounded-lg transition-all ${
                    isDragging 
                      ? 'border-primary bg-primary/10 scale-[1.02]' 
                      : 'border-muted-foreground/25 hover:border-primary hover:bg-muted/50'
                  }`}
                >
                  <label className="flex flex-col items-center justify-center gap-2 p-4 cursor-pointer">
                    <input 
                      type="file" 
                      className="hidden"
                      multiple
                      onChange={(e) => {
                        if (e.target.files?.length > 0) {
                          handleAddPendingAttachments(e.target.files);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Upload className={`w-6 h-6 ${isDragging ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="text-center">
                      <span className={`text-sm font-medium ${isDragging ? 'text-primary' : 'text-muted-foreground'}`}>
                        {isDragging ? 'Drop files here!' : 'Drag & drop files or click to browse'}
                      </span>
                      <p className="text-xs text-muted-foreground mt-1">Max 10MB per file • Multiple files allowed</p>
                    </div>
                  </label>
                </div>
                {pendingAttachments.length > 0 && (
                  <div className="space-y-1 mt-2">
                    <p className="text-xs text-muted-foreground">{pendingAttachments.length} file(s) selected:</p>
                    {pendingAttachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className="w-4 h-4 flex-shrink-0" />
                          <span className="truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">({formatFileSize(file.size)})</span>
                        </div>
                        {uploadProgress[index] !== undefined && uploadProgress[index] < 100 && (
                          <div className="flex items-center gap-2 mx-2">
                            <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-primary transition-all duration-300"
                                style={{ width: `${uploadProgress[index]}%` }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground">{uploadProgress[index]}%</span>
                          </div>
                        )}
                        <Button 
                          type="button"
                          size="sm" 
                          variant="ghost" 
                          onClick={() => removePendingAttachment(index)}
                          className="h-6 w-6 p-0 flex-shrink-0"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateTask} disabled={creating} data-testid="save-task-btn">
                {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Create Task
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      
      <CardContent>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="open" data-testid="tasks-tab-open">
              <Clock className="w-4 h-4 mr-1" />
              Open
            </TabsTrigger>
            <TabsTrigger value="overdue" data-testid="tasks-tab-overdue">
              <AlertTriangle className="w-4 h-4 mr-1" />
              Overdue
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tasks-tab-completed">
              <CheckCircle className="w-4 h-4 mr-1" />
              Completed
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : tasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <CheckSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No {activeTab} tasks</p>
              {activeTab === 'open' && (
                <Button 
                  variant="link" 
                  className="mt-2" 
                  onClick={() => setShowCreateDialog(true)}
                >
                  Create your first task
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map(task => (
                <div 
                  key={task.id} 
                  className={`p-3 rounded-lg border ${task.is_overdue ? 'border-red-300 bg-red-50 dark:bg-red-900/20' : 'border-border bg-card'}`}
                  data-testid={`task-item-${task.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getPriorityColor(task.priority)} data-testid={`task-priority-${task.id}`}>
                          {task.priority}
                        </Badge>
                        <h4 
                          className="font-medium truncate cursor-pointer hover:text-primary hover:underline"
                          onClick={() => openViewDialog(task)}
                          title="Click to view task details"
                        >
                          {task.title}
                        </h4>
                      </div>
                      {task.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-2">{task.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                        {task.due_date && (
                          <span className={`flex items-center gap-1 ${task.is_overdue ? 'text-red-600 font-medium' : ''}`}>
                            <Calendar className="w-3 h-3" />
                            Due: {formatDate(task.due_date)}
                          </span>
                        )}
                        {task.assigned_to_name && task.assigned_to !== user?.id && (
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {task.assigned_to_name}
                          </span>
                        )}
                        {task.participant_name && (
                          <Link 
                            to={`/participants/${task.participant_id}`} 
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <LinkIcon className="w-3 h-3" />
                            {task.participant_name}
                          </Link>
                        )}
                        {task.link?.url && (
                          <Link to={task.link.url} className="flex items-center gap-1 text-primary hover:underline" data-testid={`task-link-${task.id}`}>
                            <LinkIcon className="w-3 h-3" />
                            {task.link.label}
                          </Link>
                        )}
                        {task.progress_notes?.length > 0 && (
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {task.progress_notes.length} note{task.progress_notes.length !== 1 ? 's' : ''}
                          </span>
                        )}
                        {task.attachments?.length > 0 && (
                          <button 
                            className="flex items-center gap-1 text-primary hover:underline cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              openAttachmentsDialog(task);
                            }}
                            title="View/Download attachments"
                          >
                            <Paperclip className="w-3 h-3" />
                            {task.attachments.length} file{task.attachments.length !== 1 ? 's' : ''}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {task.attachments?.length > 0 && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => openAttachmentsDialog(task)}
                          title="View/Download attachments"
                          className="text-blue-600 hover:text-blue-700"
                          data-testid={`attachments-task-${task.id}`}
                        >
                          <Paperclip className="w-4 h-4" />
                        </Button>
                      )}
                      {task.status !== 'completed' && (
                        <>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleStatusChange(task.id, 'completed')}
                            title="Mark as complete"
                            data-testid={`complete-task-${task.id}`}
                          >
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => openNoteDialog(task)}
                            title="Add progress note"
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      <Button 
                        size="sm" 
                        variant="ghost"
                        onClick={() => openEditDialog(task)}
                        title="Edit task"
                        data-testid={`edit-task-${task.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {(task.created_by === user?.id || canAssignToOthers) && (
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => handleDeleteTask(task.id)}
                          title="Delete task"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Tabs>
      </CardContent>

      {/* Edit Task Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          {editingTask && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={editingTask.title}
                  onChange={(e) => setEditingTask(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingTask.description || ''}
                  onChange={(e) => setEditingTask(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={editingTask.status} 
                    onValueChange={(val) => setEditingTask(prev => ({ ...prev, status: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {taskStatuses.map((s) => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select 
                    value={editingTask.priority} 
                    onValueChange={(val) => setEditingTask(prev => ({ ...prev, priority: val }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {taskPriorities.map((p) => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={editingTask.due_date?.split('T')[0] || ''}
                  onChange={(e) => setEditingTask(prev => ({ ...prev, due_date: e.target.value }))}
                />
              </div>
              {editingTask.progress_notes?.length > 0 && (
                <div className="space-y-2">
                  <Label>Progress Notes</Label>
                  <div className="max-h-32 overflow-y-auto space-y-2 p-2 bg-muted rounded-lg">
                    {editingTask.progress_notes.map((note, idx) => (
                      <div key={idx} className="text-sm">
                        <span className="font-medium">{note.created_by_name}</span>
                        <span className="text-muted-foreground mx-1">-</span>
                        <span className="text-xs text-muted-foreground">{formatDate(note.created_at)}</span>
                        <p className="mt-1">{note.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Attachments Section */}
              <div className="space-y-2">
                <Label>Attachments</Label>
                
                {/* Existing Attachments */}
                {editingTask.attachments?.length > 0 && (
                  <div className="space-y-1 mb-2">
                    <p className="text-xs text-muted-foreground">Existing files:</p>
                    {editingTask.attachments.map((attachment) => (
                      <div key={attachment.id} className="flex items-center justify-between p-2 bg-muted rounded text-sm">
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          <span className="truncate max-w-[180px]">{attachment.file_name}</span>
                          <span className="text-xs text-muted-foreground">({formatFileSize(attachment.file_size)})</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            type="button"
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDownloadAttachment(attachment)}
                            className="h-6 w-6 p-0"
                            title="Download"
                          >
                            <Download className="w-3 h-3" />
                          </Button>
                          <Button 
                            type="button"
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleDeleteExistingAttachment(attachment.id)}
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Upload New Attachment */}
                <div
                  onDragOver={handleDragOverEdit}
                  onDragLeave={handleDragLeaveEdit}
                  onDrop={handleDropEdit}
                  className={`relative border-2 border-dashed rounded-lg transition-all ${
                    isDraggingEdit 
                      ? 'border-primary bg-primary/10 scale-[1.02]' 
                      : 'border-muted-foreground/25 hover:border-primary hover:bg-muted/50'
                  }`}
                >
                  <label className="flex flex-col items-center justify-center gap-2 p-3 cursor-pointer">
                    <input 
                      type="file" 
                      className="hidden"
                      multiple
                      onChange={(e) => {
                        if (e.target.files?.length > 0) {
                          handleAddEditAttachments(e.target.files);
                          e.target.value = '';
                        }
                      }}
                    />
                    <Upload className={`w-5 h-5 ${isDraggingEdit ? 'text-primary' : 'text-muted-foreground'}`} />
                    <div className="text-center">
                      <span className={`text-sm ${isDraggingEdit ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                        {isDraggingEdit ? 'Drop files here!' : 'Drag & drop or click to add files'}
                      </span>
                      <p className="text-xs text-muted-foreground">Max 10MB per file</p>
                    </div>
                  </label>
                </div>
                
                {/* Pending New Attachments */}
                {pendingEditAttachments.length > 0 && (
                  <div className="space-y-1 mt-2">
                    <p className="text-xs text-muted-foreground">New files to upload ({pendingEditAttachments.length}):</p>
                    {pendingEditAttachments.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded text-sm">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <FileText className="w-4 h-4 text-green-600 flex-shrink-0" />
                          <span className="truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">({formatFileSize(file.size)})</span>
                        </div>
                        {uploadProgress[`edit_${index}`] !== undefined && uploadProgress[`edit_${index}`] < 100 && (
                          <div className="flex items-center gap-2 mx-2">
                            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-green-500 transition-all duration-300"
                                style={{ width: `${uploadProgress[`edit_${index}`]}%` }}
                              />
                            </div>
                            <span className="text-xs text-green-600">{uploadProgress[`edit_${index}`]}%</span>
                          </div>
                        )}
                        <Button 
                          type="button"
                          size="sm" 
                          variant="ghost" 
                          onClick={() => removeEditPendingAttachment(index)}
                          className="h-6 w-6 p-0 flex-shrink-0"
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={() => handleUpdateTask(null)} disabled={saving} data-testid="save-task-changes-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Task Comment Dialog */}
      <Dialog open={showReopenDialog} onOpenChange={(open) => {
        if (!open) handleCancelReopen();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Reopen Completed Task
            </DialogTitle>
            <DialogDescription>
              You are changing a completed task back to <strong className="text-amber-600">{pendingReopenStatus?.replace('_', ' ')}</strong>. 
              Please provide a reason for reopening this task. This comment will be included in the notification email sent to the assignee.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="p-3 bg-muted rounded-lg">
              <p className="text-sm font-medium">{editingTask?.title}</p>
              {editingTask?.description && (
                <p className="text-xs text-muted-foreground mt-1">{editingTask.description}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="reopening-comment">Reason for Reopening <span className="text-destructive">*</span></Label>
              <Textarea
                id="reopening-comment"
                value={reopeningComment}
                onChange={(e) => setReopeningComment(e.target.value)}
                placeholder="Explain why this task needs to be reopened..."
                rows={4}
                data-testid="reopening-comment-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelReopen}>
              Cancel
            </Button>
            <Button 
              onClick={handleConfirmReopen} 
              disabled={saving || !reopeningComment.trim()}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-reopen-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reopen Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Progress Note</DialogTitle>
            <DialogDescription>
              Add a note to track progress on: {addingNoteTask?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Enter your progress update..."
              rows={4}
              data-testid="task-note-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNoteDialog(false)}>Cancel</Button>
            <Button onClick={handleAddNote} disabled={addingNote || !noteContent.trim()}>
              {addingNote && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Note
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Attachments Dialog */}
      <Dialog open={showAttachmentsDialog} onOpenChange={setShowAttachmentsDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              Task Attachments
            </DialogTitle>
            <DialogDescription>
              {viewingTask?.title}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            {viewingTask?.attachments?.length > 0 ? (
              <div className="space-y-2">
                {viewingTask.attachments.map((attachment) => (
                  <div 
                    key={attachment.id} 
                    className="flex items-center justify-between p-3 border rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <FileText className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{attachment.file_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatFileSize(attachment.file_size)} • {attachment.uploaded_by_name}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDownloadAttachment(attachment)}
                        title="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteAttachment(viewingTask.id, attachment.id)}
                        className="text-red-600 hover:text-red-700"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Paperclip className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No attachments yet</p>
              </div>
            )}
            
            {/* Upload new attachment */}
            <div className="mt-4 pt-4 border-t">
              <Label className="text-sm font-medium">Add New Attachment</Label>
              <label className="flex items-center justify-center gap-2 mt-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors">
                <input 
                  type="file" 
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.[0] && viewingTask) {
                      handleFileUpload(viewingTask.id, e.target.files[0]);
                      e.target.value = '';
                    }
                  }}
                  disabled={uploadingAttachment}
                />
                {uploadingAttachment ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span className="text-sm">Uploading...</span>
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Click to upload a file (max 10MB)</span>
                  </>
                )}
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAttachmentsDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Task Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Badge className={getPriorityColor(viewingTask?.priority)}>
                {viewingTask?.priority}
              </Badge>
              <Badge variant={viewingTask?.status === 'completed' ? 'secondary' : 'outline'}>
                {viewingTask?.status?.replace(/_/g, ' ')}
              </Badge>
            </div>
            <DialogTitle className="text-xl mt-2">{viewingTask?.title}</DialogTitle>
          </DialogHeader>
          
          {viewingTask && (
            <div className="space-y-4">
              {/* Description */}
              {viewingTask.description && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs uppercase">Description</Label>
                  <p className="text-sm whitespace-pre-wrap">{viewingTask.description}</p>
                </div>
              )}
              
              {/* Task Details Grid */}
              <div className="grid grid-cols-2 gap-4 py-2">
                {viewingTask.due_date && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase">Due Date</Label>
                    <p className={`text-sm flex items-center gap-1 ${viewingTask.is_overdue ? 'text-red-600 font-medium' : ''}`}>
                      <Calendar className="w-4 h-4" />
                      {formatDate(viewingTask.due_date)}
                      {viewingTask.is_overdue && <span>(Overdue)</span>}
                    </p>
                  </div>
                )}
                {viewingTask.assigned_to_name && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase">Assigned To</Label>
                    <p className="text-sm flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {viewingTask.assigned_to_name}
                    </p>
                  </div>
                )}
                {viewingTask.created_by_name && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase">Created By</Label>
                    <p className="text-sm">{viewingTask.created_by_name}</p>
                  </div>
                )}
                {viewingTask.created_at && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase">Created</Label>
                    <p className="text-sm">{formatDateTime(viewingTask.created_at)}</p>
                  </div>
                )}
                {viewingTask.status === 'completed' && viewingTask.completed_at && (
                  <div className="space-y-1">
                    <Label className="text-muted-foreground text-xs uppercase">Completed</Label>
                    <p className="text-sm flex items-center gap-1 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      {formatDateTime(viewingTask.completed_at)}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Linked Participant */}
              {viewingTask.participant_name && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs uppercase">Linked Participant</Label>
                  <Link 
                    to={`/participants/${viewingTask.participant_id}`} 
                    className="text-sm text-primary hover:underline flex items-center gap-1"
                  >
                    <LinkIcon className="w-4 h-4" />
                    {viewingTask.participant_name}
                  </Link>
                </div>
              )}
              
              {viewingTask.link?.url && (
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs uppercase">Linked Ticket</Label>
                  <Link to={viewingTask.link.url} className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="task-view-link">
                    <LinkIcon className="w-4 h-4" />
                    {viewingTask.link.label}
                  </Link>
                </div>
              )}

              {/* Attachments */}
              {viewingTask.attachments?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase">
                    Attachments ({viewingTask.attachments.length})
                  </Label>
                  <div className="space-y-1">
                    {viewingTask.attachments.map((attachment) => (
                      <div 
                        key={attachment.id} 
                        className="flex items-center justify-between p-2 bg-muted rounded"
                      >
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{attachment.file_name}</span>
                          <span className="text-xs text-muted-foreground">
                            ({formatFileSize(attachment.file_size)})
                          </span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownloadAttachment(attachment)}
                          data-testid={`download-attachment-${attachment.id}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Progress Notes */}
              {viewingTask.progress_notes?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-muted-foreground text-xs uppercase">
                    Progress Notes ({viewingTask.progress_notes.length})
                  </Label>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {viewingTask.progress_notes.map((note, idx) => (
                      <div key={idx} className="p-3 bg-muted rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-sm">{note.created_by_name}</span>
                          <span className="text-xs text-muted-foreground">
                            {formatDateTime(note.created_at)}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
            {viewingTask?.status !== 'completed' && (
              <>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowViewDialog(false);
                    openNoteDialog(viewingTask);
                  }}
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Add Note
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setShowViewDialog(false);
                    openEditDialog(viewingTask);
                  }}
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
                <Button 
                  onClick={() => {
                    handleStatusChange(viewingTask.id, 'completed');
                    setShowViewDialog(false);
                  }}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Complete
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TasksWidget;
